import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from '../contexts/AuthContext'

function MyProfile() {
  const { user } = useAuth()
  const navigate = useNavigate()
  
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  // Profile data
  const [username, setUsername] = useState('')
  const [fullName, setFullName] = useState('')
  const [bio, setBio] = useState('')
  const [location, setLocation] = useState('')
  const [publicEmail, setPublicEmail] = useState('')
  const [profileIsPublic, setProfileIsPublic] = useState(true)
  const [showLocation, setShowLocation] = useState(true)
  const [showEmail, setShowEmail] = useState(false)
  const [avatarUrl, setAvatarUrl] = useState('')
  
  // Image upload state
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [uploading, setUploading] = useState(false)
  
  // Collection stats
  const [stats, setStats] = useState(null)

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }
    fetchProfile()
    fetchCollectionStats()
  }, [user, navigate])

  const fetchProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single()

      if (error) throw error

      if (data) {
        setUsername(data.username || '')
        setFullName(data.full_name || '')
        setBio(data.bio || '')
        setLocation(data.location || '')
        setPublicEmail(data.public_email || '')
        setProfileIsPublic(data.profile_is_public !== false)
        setShowLocation(data.show_location !== false)
        setShowEmail(data.show_email === true)
        setAvatarUrl(data.avatar_url || '')
      }

      setLoading(false)
    } catch (error) {
      console.error('Error fetching profile:', error)
      setError('Failed to load profile')
      setLoading(false)
    }
  }

  const fetchCollectionStats = async () => {
    try {
      const { data, error } = await supabase
        .from('user_collection_summary')
        .select('*')
        .eq('user_id', user.id)
        .single()

      if (error && error.code !== 'PGRST116') throw error

      setStats(data)
    } catch (error) {
      console.error('Error fetching stats:', error)
    }
  }

  const handleImageSelect = (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file')
      return
    }

    // Validate file size (max 2MB for profile pics)
    if (file.size > 2 * 1024 * 1024) {
      setError('Image must be less than 2MB')
      return
    }

    setImageFile(file)
    
    // Create preview
    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result)
    }
    reader.readAsDataURL(file)
    setError('')
  }

  const uploadImage = async () => {
    if (!imageFile) return avatarUrl

    setUploading(true)

    try {
      // Delete old avatar if exists
      if (avatarUrl) {
        const oldPath = avatarUrl.split('/profile-pictures/')[1]
        if (oldPath) {
          await supabase.storage
            .from('profile-pictures')
            .remove([oldPath])
        }
      }

      // Create unique filename in user's folder
      const fileExt = imageFile.name.split('.').pop()
      const fileName = `${user.id}/avatar-${Date.now()}.${fileExt}`

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(fileName, imageFile)

      if (uploadError) throw uploadError

      // Get public URL
      const { data: publicURL } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(fileName)

      setUploading(false)
      return publicURL.publicUrl

    } catch (err) {
      console.error('Error uploading image:', err)
      setUploading(false)
      setError('Failed to upload image')
      return avatarUrl // Return existing URL on error
    }
  }

  const handleRemoveImage = () => {
    setImageFile(null)
    setImagePreview(null)
  }

  const handleDeleteAvatar = async () => {
    if (!window.confirm('Delete your profile picture?')) return

    try {
      // Delete from storage
      if (avatarUrl) {
        const path = avatarUrl.split('/profile-pictures/')[1]
        if (path) {
          await supabase.storage
            .from('profile-pictures')
            .remove([path])
        }
      }

      // Update database
      const { error } = await supabase
        .from('users')
        .update({ avatar_url: null })
        .eq('id', user.id)

      if (error) throw error

      setAvatarUrl('')
      setSuccess('Profile picture deleted')
      setTimeout(() => setSuccess(''), 3000)
    } catch (error) {
      console.error('Error deleting avatar:', error)
      setError('Failed to delete profile picture')
    }
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    setSuccess('')

    try {
      // Upload image first if new one selected
      let newAvatarUrl = avatarUrl
      if (imageFile) {
        newAvatarUrl = await uploadImage()
      }

      const { error } = await supabase
        .from('users')
        .update({
          username: username.trim(),
          full_name: fullName.trim() || null,
          bio: bio.trim() || null,
          location: location.trim() || null,
          public_email: publicEmail.trim() || null,
          profile_is_public: profileIsPublic,
          show_location: showLocation,
          show_email: showEmail,
          avatar_url: newAvatarUrl
        })
        .eq('id', user.id)

      if (error) throw error

      setAvatarUrl(newAvatarUrl)
      setImageFile(null)
      setImagePreview(null)
      setSuccess('Profile updated successfully!')
      setTimeout(() => setSuccess(''), 3000)
    } catch (error) {
      console.error('Error updating profile:', error)
      setError(error.message || 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="loading">Loading profile...</div>
  }

  return (
    <div className="my-profile-page">
      <div className="profile-header">
        <h1>My Profile</h1>
        <p className="page-subtitle">Manage your public profile information</p>
        <Link to={`/profile/${user.id}`} className="btn-secondary">
          View Public Profile →
        </Link>
      </div>

      {error && <div className="error-message">❌ {error}</div>}
      {success && <div className="success-message">✅ {success}</div>}

      <div className="profile-content-grid">
        {/* Left: Edit Form */}
        <div className="profile-edit-section">
          <form onSubmit={handleSave} className="profile-form">
            
            {/* Profile Picture Section */}
            <div className="form-section">
              <h2>Profile Picture</h2>

              <div className="profile-picture-upload">
                {/* Current or Preview Image */}
                <div className="current-avatar-display">
                  {imagePreview ? (
                    <div className="avatar-preview-container">
                      <img src={imagePreview} alt="Preview" className="avatar-preview" />
                      <button
                        type="button"
                        onClick={handleRemoveImage}
                        className="btn-remove-avatar-preview"
                      >
                        ✕
                      </button>
                      <p className="avatar-label">New picture (not saved yet)</p>
                    </div>
                  ) : avatarUrl ? (
                    <div className="avatar-current-container">
                      <img src={avatarUrl} alt="Current avatar" className="avatar-current" />
                      <p className="avatar-label">Current picture</p>
                      <button
                        type="button"
                        onClick={handleDeleteAvatar}
                        className="btn-delete-avatar"
                      >
                        Delete Picture
                      </button>
                    </div>
                  ) : (
                    <div className="avatar-placeholder-large">
                      {username?.charAt(0).toUpperCase() || '?'}
                      <p className="avatar-label">No picture set</p>
                    </div>
                  )}
                </div>

                {/* Upload Input */}
                <div className="avatar-upload-controls">
                  <label htmlFor="avatar-upload" className="avatar-upload-label">
                    📷 {avatarUrl ? 'Change Picture' : 'Upload Picture'}
                  </label>
                  <input
                    type="file"
                    id="avatar-upload"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="file-input"
                  />
                  <p className="field-hint">
                    JPG, PNG, or WEBP. Max 2MB. Square images work best (e.g., 400x400px).
                  </p>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h2>Basic Information</h2>

              <div className="form-group">
                <label htmlFor="username">Username *</label>
                <input
                  type="text"
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="fullName">Full Name</label>
                <input
                  type="text"
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="form-input"
                  placeholder="Optional"
                />
              </div>

              <div className="form-group">
                <label htmlFor="bio">Bio / About Me</label>
                <textarea
                  id="bio"
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                  className="form-textarea"
                  rows="4"
                  placeholder="Tell the community about yourself, your fishing style, favorite techniques..."
                  maxLength="500"
                ></textarea>
                <small className="char-count">{bio.length}/500 characters</small>
              </div>
            </div>

            <div className="form-section">
              <h2>Location & Contact</h2>

              <div className="form-group">
                <label htmlFor="location">Location</label>
                <input
                  type="text"
                  id="location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="form-input"
                  placeholder="e.g., Indianapolis, IN"
                />
                <small className="field-hint">Helps others find local trading partners</small>
              </div>

              <div className="form-group">
                <label htmlFor="publicEmail">Public Contact Email</label>
                <input
                  type="email"
                  id="publicEmail"
                  value={publicEmail}
                  onChange={(e) => setPublicEmail(e.target.value)}
                  className="form-input"
                  placeholder="Optional - for trading inquiries"
                />
                <small className="field-hint">
                  Different from login email. Only shown if you enable below.
                </small>
              </div>
            </div>

            <div className="form-section">
              <h2>Privacy Settings</h2>

              <div className="privacy-options">
                <label className="checkbox-label-profile">
                  <input
                    type="checkbox"
                    checked={profileIsPublic}
                    onChange={(e) => setProfileIsPublic(e.target.checked)}
                  />
                  <div className="checkbox-text">
                    <strong>Make my profile public</strong>
                    <p>Allow other users to view your profile and collection stats</p>
                  </div>
                </label>

                <label className="checkbox-label-profile">
                  <input
                    type="checkbox"
                    checked={showLocation}
                    onChange={(e) => setShowLocation(e.target.checked)}
                    disabled={!profileIsPublic}
                  />
                  <div className="checkbox-text">
                    <strong>Show my location</strong>
                    <p>Display location on public profile</p>
                  </div>
                </label>

                <label className="checkbox-label-profile">
                  <input
                    type="checkbox"
                    checked={showEmail}
                    onChange={(e) => setShowEmail(e.target.checked)}
                    disabled={!profileIsPublic}
                  />
                  <div className="checkbox-text">
                    <strong>Show my public contact email</strong>
                    <p>Allow users to contact you for trades</p>
                  </div>
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={saving || uploading}>
                {uploading ? 'Uploading Image...' : saving ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </form>
        </div>

        {/* Right: Stats Preview */}
        <div className="profile-stats-sidebar">
          <div className="stats-preview-card">
            <h3>Your Collection Stats</h3>
            
            {stats ? (
              <>
                <div className="stat-preview-item">
                  <span className="stat-preview-number">{stats.total_items || 0}</span>
                  <span className="stat-preview-label">Total Entries</span>
                </div>
                <div className="stat-preview-item">
                  <span className="stat-preview-number">{stats.owned_count || 0}</span>
                  <span className="stat-preview-label">Owned</span>
                </div>
                <div className="stat-preview-item">
                  <span className="stat-preview-number">{stats.for_trade_count || 0}</span>
                  <span className="stat-preview-label">For Trade</span>
                </div>
                <div className="stat-preview-item">
                  <span className="stat-preview-number">{stats.wishlist_count || 0}</span>
                  <span className="stat-preview-label">Wishlist</span>
                </div>
                <div className="stat-preview-item">
                  <span className="stat-preview-number">{stats.want_in_trade_count || 0}</span>
                  <span className="stat-preview-label">Want in Trade</span>
                </div>
              </>
            ) : (
              <p className="no-stats">No collection data yet</p>
            )}
          </div>

          <div className="profile-tips-card">
            <h3>Profile Tips</h3>
            <ul>
              <li>✅ Upload a profile picture</li>
              <li>✅ Add a location to find local traders</li>
              <li>✅ Write a bio to introduce yourself</li>
              <li>✅ Enable public email if you want trade inquiries</li>
              <li>✅ Keep privacy settings updated</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MyProfile